import React from 'react';
import Table from "src/componets/Table";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { ButtonEdit } from "src/componets/Buttons";
import { setEditId, PhoneNumberPriceInterface } from "src/store/callrecorder/phoneNumberPricesSlice";
import { EntityId } from "@reduxjs/toolkit";
import usePermissions from "src/hooks/usePermissions";
import { formattedDate } from "src/helpers/DateHelpers";
import { priceWithSymbol } from "src/helpers/CurrencyHelper";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";

import { Space, Tag, Tooltip } from "antd";
import {
  PhoneOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from "@ant-design/icons";

interface ListTableInterface {
  className?: string;
  limit: number;
}

const _ListTable = ({ className, limit }: ListTableInterface) => {
  const dispatch = useAppDispatch();
  const ids = useAppSelector(state => state.callrecorder.phoneNumberPrices.ids);
  const prices = useAppSelector(state => state.callrecorder.phoneNumberPrices.entities);
  const count = useAppSelector(state => state.callrecorder.phoneNumberPrices.count);
  const { write_callrecorder } = usePermissions();
  const data = ids ? ids.map(id => prices[id]) : [];

  function onClickEdit(id: EntityId) {
    dispatch(setEditId(id));
  }

  const typeColor: Record<string, string> = {
    local: 'blue',
    mobile: 'green',
    national: 'orange',
    tollfree: 'purple',
  };

  const renderCapability = (value: boolean, domesticOnly?: boolean) => {
    if (!value) return <CloseCircleOutlined style={{ color: '#d9d9d9' }} />;
    if (domesticOnly) return <CheckCircleOutlined style={{ color: '#faad14' }} />;
    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  };

  const capTooltip = (label: string, domesticOnly: boolean) =>
    domesticOnly ? `${label} (domestic only)` : label;

  const smsTooltip = ({ sms_enabled, raw_sms_enabled, domestic_sms_only }: PhoneNumberPriceInterface) => {
    const overridden = raw_sms_enabled !== null && raw_sms_enabled !== sms_enabled;
    const label = overridden ? `SMS (overridden, provider: ${raw_sms_enabled ? 'yes' : 'no'})` : 'SMS';
    return capTooltip(label, domestic_sms_only);
  };

  const boolTag = (val: boolean) =>
    val ? <Tag color="success">yes</Tag> : <Tag color="default">no</Tag>;

  const columns: ColumnsType<PhoneNumberPriceInterface> = [
    {
      key: 'country_code',
      title: 'Country',
      render: (_, { country_code, prefix, url }) => (
        <OptionalLink link={url}>
          {country_code} <small style={{ color: '#999' }}>+{prefix}</small>
        </OptionalLink>
      ),
      width: 90
    },

    {
      key: 'phone_number_type',
      title: 'Type',
      render: (_, { phone_number_type }) => (
        <Tag color={typeColor[phone_number_type] || 'default'}>{phone_number_type}</Tag>
      ),
      width: 90
    },

    {
      key: 'monthly_fee',
      title: 'Monthly Fee',
      render: (_, { monthly_fee, currency }) => priceWithSymbol(monthly_fee, currency),
      width: 100
    },

    {
      key: 'inbound_sms_price',
      title: 'SMS Price',
      render: (_, { inbound_sms_price, currency }) => priceWithSymbol(inbound_sms_price, currency),
      width: 90
    },

    {
      key: 'capabilities',
      title: 'Capabilities',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title={capTooltip('Trunking', record.domestic_voice_only)}>
            <span><PhoneOutlined /> {renderCapability(record.trunking_enabled, record.domestic_voice_only)}</span>
          </Tooltip>
          <Tooltip title={smsTooltip(record)}>
            <span><MessageOutlined /> {renderCapability(record.sms_enabled, record.domestic_sms_only)}</span>
          </Tooltip>
          <Tooltip title={capTooltip('MMS', record.domestic_sms_only)}>
            <span><MessageOutlined rotate={135} /> {renderCapability(record.mms_enabled, record.domestic_sms_only)}</span>
          </Tooltip>
        </div>
      ),
      width: 150
    },

    {
      key: 'address_required',
      title: 'Address Req',
      render: (_, { address_required }) => boolTag(address_required),
      width: 85
    },

    {
      key: 'beta',
      title: 'Beta',
      render: (_, { beta }) => boolTag(beta),
      width: 65
    },

    {
      key: 'updated_at',
      title: 'Updated',
      render: (_, { updated_at }) => {
        return <small>{formattedDate(updated_at).dateTime}</small>;
      },
      width: 125
    },

    ...(write_callrecorder ? [{
      key: 'action',
      title: 'Action',
      render: (_: any, { id }: PhoneNumberPriceInterface) => {
        return (
          <Space size="middle">
            <ButtonEdit key={'button-edit'} onClick={() => onClickEdit(id)} />
          </Space>
        );
      },
      width: 80
    }] : []),
  ];

  return (
    <div className={className}>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
        search_keys={['country_code']}
        sort_keys={['updated_at', 'monthly_fee', 'inbound_sms_price']}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
